import { PlayNextButton, PlayPreviousButton } from "@/components/SkipButtons";
import { type ReactNode, useEffect, useRef, useState } from "react";

import BusinessCardPlayer from "@/components/BusinessCardPlayer";
import { PlaylistItem } from "../../../../APITypes/PlaylistItem.cs";
import { ProjectInfo } from "../../../../APITypes/ProjectInfo.cs";
import { VideoJsPlayerPluginOptions } from "video.js";
import { VmPlayer } from "../../shared/VmPlayer";
import { cn } from "../../../../lib/utils";
import { segment } from "../../../../APITypes/Segment.cs";
import { useDataContext } from "../../../../app/_context";
import { useSwipeable } from "react-swipeable";

export default function Carousel(props: {
  pid: string;
  meta: ProjectInfo;
  sessionPlaylist: PlaylistItem[];
  playList: PlaylistItem[];
  highlightsArray: segment[];
  videoJsOptions: VideoJsPlayerPluginOptions;
  widgetMode: boolean;
  isVirginState: boolean;
  isMuted: boolean;
  mobileBump: string;
  setMobileBump: (mobileBump: string) => void;
  setHighlightsArray: (highlights: segment[]) => void | null;
  bookLink: string;
  patreonLink: string;
  twitterLink: string;
  /** Drawn inside the player; see PlayerOverlays. */
  playerOverlay?: ReactNode;
}) {
  const {
    isBusinessCardPlaying,
    currentSessionIndex,
    startNextSession,
    startPreviousSession,
  } = useDataContext();

  const containerDiv = useRef<HTMLDivElement>(null);
  const [currentContainerDivOffsetHeight, setCurrentContainerDivOffsetHeight] =
    useState(0);
  const [isDesktopLayout, setIsDesktopLayout] = useState(false);

  useEffect(() => {
    if (!containerDiv.current) return;
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const syncDesktopLayout = () => setIsDesktopLayout(mediaQuery.matches);

    syncDesktopLayout();
    setCurrentContainerDivOffsetHeight(containerDiv.current.offsetHeight);
    mediaQuery.addEventListener("change", syncDesktopLayout);

    const observer = new ResizeObserver(() => {
      if (containerDiv.current) {
        setCurrentContainerDivOffsetHeight(containerDiv.current.offsetHeight);
      }
    });
    observer.observe(containerDiv.current);
    return () => {
      mediaQuery.removeEventListener("change", syncDesktopLayout);
      observer.disconnect();
    };
  }, []);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      startNextSession();
    },
    onSwipedRight: () => {
      startPreviousSession();
    },
    onSwipeStart: () => {
      if (currentSessionIndex === 0) {
        props.setMobileBump("Left");
        setTimeout(() => {
          props.setMobileBump("None");
        }, 700);
      }
    },
    preventDefaultTouchmoveEvent: true,
  });

  return (
    <div
      className={cn(
        "w-full flex md:h-full aspect-video md:aspect-auto",
        "bg-background",
      )}
      id="PlayerContainerDiv"
    >
      <div className="relative group h-full hidden md:flex items-center -mx-2 md:mx-0 md:-mr-2">
        <span className="relative -left-1 scale-[70%] group-hover:-left-2 transition-[left]">
          <PlayPreviousButton />
        </span>
      </div>

      <div
        className="w-full flex items-end mx-auto pt-2 md:pt-0"
        style={
          isDesktopLayout && currentContainerDivOffsetHeight > 0
            ? {
                maxWidth: `${currentContainerDivOffsetHeight * 1.5}px`,
              }
            : undefined
        }
        ref={containerDiv}
      >
        <div
          {...handlers}
          id="MainVideoDiv"
          className="w-full h-full flex place-items-center"
        >
          <div className="videoDiv w-full max-w-full">
            {isBusinessCardPlaying ? (
              <BusinessCardPlayer />
            ) : (
              <VmPlayer portal={false} overlay={props.playerOverlay} />
            )}
            {/* <div className="md:hidden text-sm pl-3 -mb-2 mt-1">
              <h3 className="truncate pr-3 text-left pb-1 text-muted">
                {props.meta.title}
              </h3>
            </div> */}
          </div>
        </div>
      </div>

      <div className="relative h-full items-center -mx-2 md:mx-0 md:-ml-2 hidden md:flex group">
        <span className="relative left-3 scale-[70%] group-hover:left-4 transition-[left]">
          <PlayNextButton />
        </span>
      </div>
    </div>
  );
}
